// import { NULL_EXPR } from '@angular/compiler/src/output/output_ast';

import { ExceptionService } from '../services/exception.service';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import moment from 'moment';
import * as _ from 'lodash-es';
import { NodeAPIService } from 'app/services/node.api.service';

@Component({
    selector: 'recap-applicant-address',
    templateUrl: './recap-applicant-address.component.html',
    styleUrls: ['./recap-applicant-address.component.scss'],
    standalone: false
})
export class RecapApplicantAddressComponent implements OnInit {
    /**
     * Perform address matching on applicants
     *
     * @type {boolean}
     * @memberof RecapComponent
     */
    @Input() doAddressMatch: boolean = false;

    /**
     * The applicant iterator.
     *
     * @type {number} The iterator.
     * @memberof RecapApplicantAddressComponent
     */
    @Input() applicantIterator: number;

    /**
     * Did they already select an address match?
     *
     * @type {boolean}
     * @memberof RecapApplicantAddressComponent
     */
    @Input() addressMatchDoneAlready: boolean = false;

    /**
     * The address object from the applicant recap component.
     *
     * @type {Object}
     * @memberof RecapApplicantAddressComponent
     */
    @Input() address: Object;

    /**
     * The iterator of this address.
     *
     * @type {number}
     * @memberof RecapApplicantAddressComponent
     */
    @Input() iterator: number;

    /**
     * Output for the address match to the applicant component.
     *
     * @type {EventEmitter<object>}
     * @memberof RecapApplicantAddressComponent
     */
    @Output() addressMatchOut: EventEmitter<object> = new EventEmitter<object>();

    /**
     * Forms a selected address component to output to parent.
     *
     * @type {FormControl}
     * @memberof RecapApplicantAddressComponent
     */
    selectedAddress: UntypedFormControl = new UntypedFormControl(null, []);

    /**
     * Getter for address.
     *
     * @readonly
     * @type {string}
     * @memberof RecapApplicantAddressComponent
     */
    get Address(): string {
        if (_.isNil(this.address)) {
            return;
        }
        return `${this.address['HouseNumber']} ${this.address['HouseName']}
         ${this.address['Street']}
         ${this.address['PostTown']} ${this.address['County']} ${this.address['Postcode']}`;
    }



    /**
     * Getter for time at address.
     *
     * @readonly
     * @memberof RecapApplicantAddressComponent
     */
    get TimeAtAddress() {
        if (_.isNil(this.address)) {
            return;
        }
        if (_.isNil(this.address['EffectiveFromDate'])) {
            return 'More than 3 years';
        }
        const date = this.address['EffectiveFromDate'];
        const diff = moment().diff(moment(date), 'year');
        if (diff === 0) {
            return 'Less than 1 year';
        }
        if (diff === 1) {
            return `${diff} year`;
        }
        return `${diff} years`;
    }


    /**
     * Does this need an address match?
     *
     * @readonly
     * @type {boolean}
     * @memberof RecapApplicantAddressComponent
     */
    get NeedsAddressMatch(): boolean {
        return _.isNil(this.address['PtcAbsCode']) && this.doAddressMatch;
    }

    /**
     * Formats the address match for the radio list.
     *
     * @param {object} matchAddress
     * @returns {string}
     * @memberof RecapApplicantAddressComponent
     */
    formatMatchAddress(matchAddress: object): string {
        return `${matchAddress['HouseNo']} ${matchAddress['HouseName']}
        ${matchAddress['Street1']} ${matchAddress['Street2']}
         ${matchAddress['PostTown']} ${matchAddress['County']} ${matchAddress['Postcode']}`;
    }

    /**
     * Outputs the address to the applicant component.
     *
     * @param {string} [ptcAbsCode=null]
     * @memberof RecapApplicantAddressComponent
     */
    outputAddress(ptcAbsCode: string = null) {

        // Set the code in the control.
        this.selectedAddress.setValue(ptcAbsCode);

        // Emit the change to the recap component.
        this.addressMatchOut.emit({
            iterator: this.iterator,
            applicant: this.applicantIterator,
            address: this.selectedAddress,
        });

    }

    /**
     * Sets the ptcabscode for the selected address.
     *
     * @param {string} ptcAbsCode
     * @memberof RecapApplicantAddressComponent
     */
    setPtcAbsCode(ptcAbsCode: string): void {
        this.outputAddress(ptcAbsCode);

        // *TODO* Update database with PtcAbsCode for address
        this.api.saveAddressMatch(this.address['Id'], ptcAbsCode)
            .subscribe(
                (success) => {
                    this.address['PtcAbsCode'] = ptcAbsCode;
                },
                (error) => {
                    this.exception.haveReceivedError(error);
                },
            );
    }

    /**
     * Defaults the control state based upon the ptcabscode stuff.
     *
     * @memberof RecapApplicantAddressComponent
     */
    defaultControlState() {
        let validatorsRequired = [];

        if (this.NeedsAddressMatch && !this.addressMatchDoneAlready) {
            validatorsRequired.push(Validators.required);
        }

        this.selectedAddress.setValidators(validatorsRequired);
        this.selectedAddress.updateValueAndValidity();
    }

    /**
     * Implements ngOnInit
     *
     * @memberof RecapApplicantAddressComponent
     */
    ngOnInit() {
        this.defaultControlState();
        this.outputAddress();
    }


    /**
     * Creates an instance of RecapApplicantAddressComponent.
     * @param {NodeAPIService} api
     * @param {ExceptionService} exception
     * @memberof RecapApplicantAddressComponent
     */
    constructor(private api: NodeAPIService, private exception: ExceptionService) { }
}
